import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShortlistedGet, ShortlistedRemove, getInterests, getUserActivePlans } from '../../Api/CoreApi';
import {
  FaHeart, FaComment, FaEye, FaUsers, FaSearch, FaDownload,
  FaTrash, FaArrowLeft, FaArrowRight, FaSpinner, FaLock, FaTimes, FaPhone
} from 'react-icons/fa';
import { fetchShortlistedUsers, addShortlistListener } from '../../utils/shortlistUtils';
import { message } from 'antd';
import { BASE_API } from "../../Api/Api";

const Shortlisted = () => {
  const navigate = useNavigate();
  const BASE_URL = BASE_API;
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [shortlistedData, setShortlistedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [interestSentIds, setInterestSentIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ===== PLAN & LOCK STATE =====
  const [activePlans, setActivePlans] = useState([]);
  const [viewedContacts, setViewedContacts] = useState([]);
  const [showLockModal, setShowLockModal] = useState(false);
  const isLoggedIn = !!localStorage.getItem('access_token');

  useEffect(() => {
    fetchShortlisted();
    fetchInterests();
    fetchUserPlans();
  }, []);

  // ===== FETCH PLANS =====
  const fetchUserPlans = async () => {
    if (!isLoggedIn) return;
    try {
      const data = await getUserActivePlans();
      setActivePlans(data?.plans || []);
      setViewedContacts(data?.viewed_contacts || []);
    } catch (err) {
      console.log("Plan fetch error:", err);
      setActivePlans([]);
      setViewedContacts([]);
    }
  };

  // ===== PLAN CHECK LOGIC =====
  const hasActivePlan = () => isLoggedIn && activePlans.length > 0;
  const isProfileUnlocked = (userId) => viewedContacts.includes(String(userId));

  const canSendInterest = (userId) => {
    if (interestSentIds.includes(String(userId))) return true;
    if (isProfileUnlocked(String(userId))) return true;
    if (hasActivePlan()) return true;
    return false;
  };

  // ===== FETCH INTERESTS =====
  const fetchInterests = async () => {
    try {
      const interests = await getInterests();
      const ids = interests.map(item => item.to_user || item.user_id || item.target_user_id);
      setInterestSentIds(ids);
    } catch (err) {
      console.log("Interest fetch error:", err);
    }
  };

  // ===== FETCH SHORTLISTED =====
  const fetchShortlisted = async () => {
    setLoading(true);
    try {
      console.log("🔄 Fetching shortlisted data directly...");
      const data = await ShortlistedGet();
      console.log("📦 Raw Shortlisted Data:", data);

      if (Array.isArray(data) && data.length > 0) {
        console.log(`✅ Found ${data.length} shortlisted users`);

        const transformedData = data.map((item, index) => {
          // ✅ Get user data object safely
          const userData = item.user || item.user_data || item.profile || {};

          // ✅ Get user ID from multiple possible fields
          const userId = item.target_user_id || item.user_id || item.id || userData.id;

          console.log(`📝 Processing item ${index}:`, item);
          console.log(`📝 User data:`, userData);

          let profileId = item.profile_id || userData.profile_id || null;

          if (!profileId && item.user && item.user.profile_id) {
            profileId = item.user.profile_id;
          }

          // ✅ Get name from multiple possible sources
          const name = userData.firstname ||
            userData.first_name ||
            userData.username ||
            item.name ||
            item.username ||
            userData.name ||
            'Unknown';

          // ✅ Get education from multiple possible fields (Exact Original Fallbacks)
          const education = userData.education ||
            userData.highest_education ||
            userData.highestEducation ||
            userData.course ||
            userData.degree ||
            userData.qualification ||
            userData.education_level ||
            item.education ||
            item.course ||
            'N/A';

          // ✅ Get occupation from multiple possible fields (Exact Original Fallbacks)
          const occupation = userData.occupation ||
            userData.job_title ||
            userData.jobTitle ||
            userData.profession ||
            userData.work ||
            userData.employment ||
            userData.designation ||
            item.occupation ||
            item.job_title ||
            'N/A';

          // ✅ Get location from multiple possible fields
          const location = userData.city && userData.state
            ? `${userData.city}, ${userData.state}`
            : userData.city ||
            userData.state ||
            userData.location ||
            item.location ||
            'N/A';

          // ✅ Get age from multiple possible fields
          const age = userData.age ||
            userData.dob ||
            item.age ||
            0;

          // ✅ Get height from multiple possible fields
          const height = userData.height ||
            userData.height_cm ||
            item.height ||
            'N/A';

          return {
            id: item.id || userId,
            target_user_id: userId,
            shortlist_id: item.id,
            created_at: item.created_at || item.created || new Date().toISOString(),

            name: name,
            bid: item.bid || userId || 'N/A',
            profile_id: profileId,
            age: age,
            height: height,
            location: location,
            education: education,
            occupation: occupation,
            image: userData.pic ||
              item.pic ||
              userData.profile_pic ||
              item.profile_pic ||
              userData.image ||
              userData.avatar ||
              null,
            online: userData.is_online ||
              item.is_online ||
              userData.online_status ||
              false,
            matchPercentage: item.matchPercentage || 75,

            shortlistedOn: item.shortlistedOn || (item.created_at || item.created
              ? new Date(item.created_at || item.created).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })
              : 'N/A'),
            shortlistedTime: item.shortlistedTime || (item.created_at || item.created
              ? new Date(item.created_at || item.created).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit'
              })
              : 'N/A'),
            isNew: index < 3,
            viewed: false,
            messages: 0
          };
        });

        console.log("✅ Transformed Data:", transformedData);
        setShortlistedData(transformedData);
      } else {
        console.warn("⚠️ No shortlisted data found or data is empty");
        setShortlistedData([]);
      }
    } catch (error) {
      console.error("❌ Error fetching shortlisted:", error);
      message.error("Failed to load shortlisted profiles");
      setShortlistedData([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== REMOVE FROM SHORTLIST =====
  const handleRemove = async (shortlistId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    console.log("🗑️ handleRemove called with ID:", shortlistId);

    if (!shortlistId) {
      console.error("❌ No shortlist ID provided");
      message.error("Invalid shortlist ID");
      return;
    }

    // ⭐ Store item before removing (for restore on error)
    const removedItem = shortlistedData.find(item => {
      const itemId = item.shortlist_id || item.id;
      return String(itemId) === String(shortlistId);
    });

    const removedIndex = shortlistedData.findIndex(item => {
      const itemId = item.shortlist_id || item.id;
      return String(itemId) === String(shortlistId);
    });

    setActionLoading(prev => ({ ...prev, [shortlistId]: 'remove' }));

    // ⭐ Optimistic UI - Remove immediately for better UX
    setShortlistedData(prev => prev.filter(item => {
      const itemId = item.shortlist_id || item.id;
      return String(itemId) !== String(shortlistId);
    }));

    try {
      const result = await ShortlistedRemove(shortlistId);
      console.log("✅ Remove result:", result);

      // ⭐ Update shortlistUtils cache
      try {
        const { fetchShortlistedUsers } = await import('../../utils/shortlistUtils');
        await fetchShortlistedUsers(true);
      } catch (utilErr) {
        console.warn("Cache update failed:", utilErr);
      }

      // ⭐ Show appropriate message
      if (result?.already_deleted) {
        message.info("ℹ️ Item was already removed");
      } else {
        message.success("✅ Removed from shortlist");
      }

      // ⭐ Adjust pagination if needed
      const newTotalItems = shortlistedData.length - 1;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }

    } catch (error) {
      console.error("❌ Error removing from shortlist:", error);

      // ⭐ RESTORE ITEM on error (rollback optimistic update)
      if (removedItem && removedIndex !== -1) {
        setShortlistedData(prev => {
          const newData = [...prev];
          newData.splice(removedIndex, 0, removedItem);
          return newData;
        });
      }

      const errorMsg = error?.response?.data?.message || 
                       error?.response?.data?.error || 
                       error?.message || 
                       "Failed to remove from shortlist";
      
      message.error(`❌ ${errorMsg}. Please try again.`);
    } finally {
      setActionLoading(prev => ({ ...prev, [shortlistId]: null }));
    }
  };

  // ===== SEND INTEREST (WITH PLAN CHECK) =====
  const handleSendInterest = async (userId, e) => {
    e.stopPropagation();

    // ✅ Check Plan logic
    if (!isLoggedIn) return navigate('/User_Login');
    if (!canSendInterest(userId)) {
      setShowLockModal(true);
      return;
    }

    setActionLoading(prev => ({ ...prev, [userId]: 'interest' }));
    try {
      const { sendInterest } = await import('../../Api/CoreApi');
      await sendInterest(userId);
      setInterestSentIds(prev => [...prev, userId]);
      message.success("💖 Interest sent successfully!");
    } catch (error) {
      console.error("Error sending interest:", error);
      message.error("Failed to send interest");
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  // ===== VIEW PROFILE =====
  const handleViewProfile = (userId) => {
    if (userId) navigate(`/user-profile/${userId}`);
  };

  // ===== STATS =====
  const stats = [
    {
      label: "Total Shortlisted",
      value: shortlistedData.length,
      icon: FaHeart,
      gradient: "linear-gradient(135deg, #ff0055, #ff4d88)"
    },
    {
      label: "Viewed",
      value: shortlistedData.filter(item => item.viewed).length || 0,
      icon: FaEye,
      gradient: "linear-gradient(135deg, #ff4d88, #ff80aa)"
    },
    {
      label: "Interests Sent",
      value: interestSentIds.length || 0,
      icon: FaUsers,
      gradient: "linear-gradient(135deg, #c2185b, #ff0055)"
    },
    {
      label: "New Profiles",
      value: shortlistedData.filter(item => item.isNew).length || 0,
      icon: FaHeart,
      gradient: "linear-gradient(135deg, #ff80aa, #ffb3c8)"
    }
  ];

  // ===== FILTER DATA =====
  const filteredData = shortlistedData.filter(item => {
    const matchesSearch = (item.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.profile_id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.bid || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.location || "").toLowerCase().includes(searchTerm.toLowerCase());

    if (filterBy === 'online') return matchesSearch && item.online;
    if (filterBy === 'offline') return matchesSearch && !item.online;
    return matchesSearch;
  });

  // ===== PAGINATION =====
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== LOCK MODAL COMPONENT =====
  const LockModal = () => (
    <div className="sl-lock-overlay" onClick={() => setShowLockModal(false)}>
      <div className="sl-lock-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sl-lock-close" onClick={() => setShowLockModal(false)}><FaTimes /></button>
        <div className="sl-lock-icon">🔒</div>
        <h3 className="sl-lock-title">Plan Required!</h3>
        <p className="sl-lock-desc">You need an active plan to send interest.</p>
        <div className="sl-lock-features">
          <div className="sl-lock-feature"><FaHeart style={{ color: '#ff0055' }} /><span>Unlimited interests</span></div>
          <div className="sl-lock-feature"><FaPhone style={{ color: '#ff0055' }} /><span>View contacts</span></div>
        </div>
        <button className="sl-lock-btn-primary" onClick={() => navigate('/recharge')}>🚀 Buy Plan</button>
        <button className="sl-lock-btn-secondary" onClick={() => setShowLockModal(false)}>Maybe Later</button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <style>{`@keyframes slSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16, background: '#fff' }}>
          <FaSpinner size={48} style={{ color: '#ff0055', animation: 'slSpin 1s linear infinite' }} />
          <p style={{ color: '#888', fontSize: 14, fontFamily: 'Poppins, sans-serif' }}>💖 Loading shortlisted profiles...</p>
        </div>
      </>
    );
  }

  if (!loading && shortlistedData.length === 0) {
    return (
      <div className="sl-empty">
        <div className="sl-empty-icon">
          <FaHeart />
        </div>
        <h3 className="sl-empty-title">No Shortlisted Profiles</h3>
        <p className="sl-empty-sub">You haven't shortlisted any profiles yet. Start exploring matches!</p>
        <button className="sl-empty-btn" onClick={() => navigate('/all-users')}>
          💖 Find Matches
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes slSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

        .sl-page { background: #fff; min-height: 100vh; padding: 20px 16px; }
        .sl-wrap { max-width: 1280px; margin: 0 auto; }

        /* Header */
        .sl-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; flex-wrap: wrap; gap: 12px; }
        .sl-title { font-size: 24px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 10px; }
        .sl-title svg { color: #ff0055; }
        .sl-subtitle { color: #888; font-size: 13.5px; margin: 4px 0 0; }
        .sl-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .sl-search-wrap { position: relative; }
        .sl-search { padding: 10px 14px 10px 40px; border: 1.5px solid #ffe4ec; border-radius: 12px; font-size: 13px; outline: none; width: 240px; transition: all .3s ease; background: #fff; }
        .sl-search:focus { border-color: #ff0055; box-shadow: 0 0 0 3px rgba(255,0,85,.08); }
        .sl-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #ff4d88; font-size: 13px; }

        .sl-filter-group { display: flex; gap: 5px; padding: 4px; background: #fff0f3; border: 1.5px solid #ffe4ec; border-radius: 12px; }
        .sl-filter-btn { padding: 7px 14px; border-radius: 9px; border: none; background: transparent; cursor: pointer; font-size: 12.5px; color: #888; font-weight: 600; transition: all .25s ease; display: inline-flex; align-items: center; gap: 5px; }
        .sl-filter-btn:hover { color: #ff0055; }
        .sl-filter-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 4px 12px rgba(255,0,85,.18); }

        .sl-export-btn { display: flex; align-items: center; gap: 6px; padding: 10px 16px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border: none; border-radius: 12px; cursor: pointer; font-size: 12.5px; font-weight: 700; box-shadow: 0 6px 18px rgba(255,0,85,.22); transition: all .3s ease; }
        .sl-export-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(255,0,85,.3); }

        /* Stats */
        .sl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }
        .sl-stat-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 16px; padding: 16px; display: flex; align-items: center; gap: 12px; transition: all .3s ease; box-shadow: 0 4px 14px rgba(255,0,85,.04); }
        .sl-stat-card:hover { transform: translateY(-3px); border-color: #ffb3c8; box-shadow: 0 10px 24px rgba(255,0,85,.1); }
        .sl-stat-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #fff; flex-shrink: 0; }
        .sl-stat-value { font-size: 22px; font-weight: 800; color: #1a0a10; margin: 0; }
        .sl-stat-label { font-size: 12px; color: #888; margin: 2px 0 0; font-weight: 500; }

        /* Table */
        .sl-table-card { background: #fff; border-radius: 18px; border: 2px solid #ffe4ec; overflow: hidden; box-shadow: 0 8px 24px rgba(255,0,85,.05); }
        .sl-table-wrap { overflow-x: auto; }
        .sl-table { width: 100%; border-collapse: collapse; min-width: 850px; }
        .sl-table thead { background: linear-gradient(135deg, #fff0f3, #ffe4ec); border-bottom: 2px solid #ffd3df; }
        .sl-table th { padding: 14px 16px; text-align: left; font-size: 11.5px; font-weight: 700; color: #c2185b; text-transform: uppercase; letter-spacing: .5px; }
        .sl-table tr { border-bottom: 1px solid #fff0f3; transition: background .25s ease; }
        .sl-table tbody tr:hover { background: #fffafc; }
        .sl-table td { padding: 14px 16px; vertical-align: middle; }

        .sl-profile-cell { display: flex; align-items: center; gap: 12px; }
        .sl-profile-img { width: 44px; height: 44px; border-radius: 50%; border: 2.5px solid #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,.18); object-fit: cover; }
        .sl-profile-name { font-size: 14px; font-weight: 700; color: #1a0a10; margin: 0; }
        .sl-profile-bid { font-size: 11.5px; color: #ff4d88; margin: 2px 0 0; font-weight: 600; }
        .sl-online-row { display: flex; align-items: center; gap: 5px; margin-top: 3px; }
        .sl-online-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
        .sl-online-text { font-size: 10.5px; font-weight: 600; }
        .sl-new-tag { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 9px; padding: 2px 8px; border-radius: 999px; font-weight: 700; margin-left: 4px; box-shadow: 0 2px 8px rgba(255,0,85,.2); }

        .sl-td-main { font-size: 13.5px; color: #444; font-weight: 600; }
        .sl-td-sub { font-size: 11.5px; color: #999; margin-top: 2px; }

        .sl-match-badge { display: inline-block; padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 800; background: linear-gradient(135deg, #fff0f3, #ffe4ec); color: #ff0055; border: 1.5px solid #ffd3df; }

        /* Actions */
        .sl-action-group { display: flex; gap: 5px; }
        .sl-action-btn { width: 32px; height: 32px; border: none; border-radius: 999px; cursor: pointer; font-size: 12px; transition: all .25s ease; display: flex; align-items: center; justify-content: center; }
        .sl-action-btn:hover:not(:disabled) { transform: translateY(-2px); }
        .sl-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        
        .sl-btn-heart { background: linear-gradient(135deg, #fff0f3, #ffe4ec); color: #ff0055; }
        .sl-btn-heart:hover:not(:disabled) { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; }
        
        .sl-btn-lock { background: #fff5f5; color: #ff5252; border: 1px solid #ffebeb; }
        .sl-btn-lock:hover { background: #ffebeb; color: #d32f2f; }

        .sl-btn-msg { background: #e3f2fd; color: #2196f3; }
        .sl-btn-msg:hover:not(:disabled) { background: linear-gradient(135deg, #2196f3, #1976d2); color: #fff; }
        .sl-btn-view { background: #e8f5e9; color: #4caf50; }
        .sl-btn-view:hover:not(:disabled) { background: linear-gradient(135deg, #4caf50, #66bb6a); color: #fff; }
        .sl-btn-del { background: #fff0f3; color: #f44336; }
        .sl-btn-del:hover:not(:disabled) { background: linear-gradient(135deg, #f44336, #e53935); color: #fff; }

        .sl-btn-interest-sent {
          background: linear-gradient(135deg, #c8e6c9, #a5d6a7);
          color: #2e7d32;
          cursor: default;
        }

        /* Lock Modal Styles */
        .sl-lock-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(5px); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 16px; }
        .sl-lock-modal { background: #fff; width: 100%; max-width: 380px; border-radius: 24px; padding: 30px 24px; text-align: center; position: relative; animation: modalPop 0.3s ease; box-shadow: 0 20px 40px rgba(0,0,0,0.2); }
        @keyframes modalPop { 0% { transform: scale(0.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        .sl-lock-close { position: absolute; top: 16px; right: 16px; background: #fff0f3; border: none; width: 32px; height: 32px; border-radius: 50%; color: #ff0055; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; }
        .sl-lock-close:hover { background: #ff0055; color: #fff; transform: rotate(90deg); }
        .sl-lock-icon { font-size: 50px; margin-bottom: 16px; filter: drop-shadow(0 4px 10px rgba(255,0,85,0.2)); }
        .sl-lock-title { font-size: 20px; font-weight: 800; color: #1a0a10; margin-bottom: 8px; }
        .sl-lock-desc { font-size: 13.5px; color: #666; margin-bottom: 24px; }
        .sl-lock-features { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; text-align: left; background: #fdfdfd; padding: 16px; border-radius: 16px; border: 1px dashed #ffe4ec; }
        .sl-lock-feature { display: flex; align-items: center; gap: 10px; font-size: 13.5px; font-weight: 600; color: #444; }
        .sl-lock-btn-primary { width: 100%; padding: 14px; border: none; border-radius: 12px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 14px; font-weight: 700; cursor: pointer; margin-bottom: 10px; box-shadow: 0 6px 16px rgba(255,0,85,0.25); transition: 0.3s; }
        .sl-lock-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(255,0,85,0.35); }
        .sl-lock-btn-secondary { width: 100%; padding: 14px; border: none; border-radius: 12px; background: transparent; color: #888; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: 0.3s; }
        .sl-lock-btn-secondary:hover { color: #444; background: #f5f5f5; }

        /* Empty */
        .sl-empty { padding: 60px 24px; text-align: center; }
        .sl-empty-icon { width: 80px; height: 80px; border-radius: 50%; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; font-size: 32px; color: #ff0055; }
        .sl-empty-title { font-size: 20px; font-weight: 800; color: #c2185b; margin: 0 0 8px; }
        .sl-empty-sub { color: #888; margin: 0 0 20px; font-size: 13.5px; }
        .sl-empty-btn { padding: 11px 28px; border-radius: 999px; border: none; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 8px 22px rgba(255,0,85,.22); transition: all .3s ease; }
        .sl-empty-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(255,0,85,.3); }

        /* Pagination */
        .sl-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; padding: 16px 4px; flex-wrap: wrap; gap: 10px; }
        .sl-page-info { font-size: 13px; color: #888; }
        .sl-page-info strong { color: #ff0055; font-weight: 800; }
        .sl-page-btns { display: flex; gap: 6px; flex-wrap: wrap; }
        .sl-page-btn { min-width: 34px; height: 34px; border-radius: 9px; border: 1.5px solid #ffe4ec; background: #fff; color: #888; cursor: pointer; font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all .25s ease; padding: 0 10px; }
        .sl-page-btn:hover { border-color: #ff0055; color: #ff0055; }
        .sl-page-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,.2); }
        .sl-page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        @media (max-width: 1024px) {
          .sl-stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .sl-header { flex-direction: column; align-items: stretch; }
          .sl-actions { width: 100%; }
          .sl-search { width: 100%; }
        }
        @media (max-width: 480px) {
          .sl-stats-grid { grid-template-columns: 1fr 1fr; }
          .sl-filter-group { width: 100%; }
          .sl-filter-btn { flex: 1; justify-content: center; }
        }
      `}</style>

      {showLockModal && <LockModal />}

      <div className="sl-page">
        <div className="sl-wrap">

          {/* Header */}
          <div className="sl-header">
            <div>
              <h1 className="sl-title">
                <FaHeart /> Shortlisted
              </h1>
              <p className="sl-subtitle">View and manage your shortlisted profiles 💖</p>
            </div>

            <div className="sl-actions">
              <div className="sl-search-wrap">
                <FaSearch className="sl-search-icon" />
                <input
                  type="text"
                  placeholder="Search by ID, Name, Location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="sl-search"
                />
              </div>

              <div className="sl-filter-group">
                <button
                  className={`sl-filter-btn ${filterBy === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterBy('all')}
                >
                  All
                </button>
                <button
                  className={`sl-filter-btn ${filterBy === 'online' ? 'active' : ''}`}
                  onClick={() => setFilterBy('online')}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4caf50' }}></span>
                  Online
                </button>
                <button
                  className={`sl-filter-btn ${filterBy === 'offline' ? 'active' : ''}`}
                  onClick={() => setFilterBy('offline')}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#bbb' }}></span>
                  Offline
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="sl-stats-grid">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="sl-stat-card">
                  <div className="sl-stat-icon" style={{ background: s.gradient }}>
                    <Icon />
                  </div>
                  <div>
                    <p className="sl-stat-value">{s.value}</p>
                    <p className="sl-stat-label">{s.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ✅ Plan Banner */}
          {activePlans.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)', border: '2px solid #ffd3df', borderRadius: '16px', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 24 }}>🔒</div>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 800, color: '#c2185b', margin: 0 }}>Purchase a plan to send interest!</p>
                  <p style={{ fontSize: 11, color: '#888', margin: 0 }}>Unlock unlimited messaging and contact views.</p>
                </div>
              </div>
              <button onClick={() => navigate('/recharge')} style={{ padding: '9px 18px', background: 'linear-gradient(135deg,#ff0055,#ff4d88)', color: '#fff', border: 'none', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Buy Plan</button>
            </div>
          )}

          {/* Table */}
          <div className="sl-table-card">
            {currentItems.length > 0 ? (
              <div className="sl-table-wrap">
                <table className="sl-table">
                  <thead>
                    <tr>
                      <th>Profile</th>
                      <th>Age / Height</th>
                      <th>Location</th>
                      <th>Education / Job</th>
                      <th>Match %</th>
                      <th>Shortlisted On</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item) => {
                      const userId = item.target_user_id || item.id;
                      const shortlistId = item.shortlist_id || item.id;
                      const isLoading = actionLoading[shortlistId] || actionLoading[userId];
                      const isInterestSent = interestSentIds.includes(userId);
                      const canInterest = canSendInterest(userId);

                      const imageSrc = item.image
                        ? item.image.startsWith('http')
                          ? item.image
                          : `${BASE_URL}${item.image}`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || 'User')}&background=ff0055&color=fff&size=128`;

                      return (
                        <tr key={shortlistId}>
                          {/* Profile */}
                          <td>
                            <div className="sl-profile-cell">
                              <img
                                src={imageSrc}
                                alt={item.name || 'User'}
                                className="sl-profile-img"
                                onError={(e) => {
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || 'User')}&background=ff0055&color=fff&size=128`;
                                }}
                              />
                              <div>
                                <p className="sl-profile-name">{item.name || 'Unknown'}</p>
                                <p className="sl-profile-bid">ID: {item.profile_id ? `${item.profile_id}` : item.profile_id || 'N/A'}</p>
                                <div className="sl-online-row">
                                  <span className="sl-online-dot" style={{ background: item.online ? '#4caf50' : '#bbb' }}></span>
                                  <span className="sl-online-text" style={{ color: item.online ? '#4caf50' : '#999' }}>
                                    {item.online ? 'Online' : 'Offline'}
                                  </span>
                                  {item.isNew && <span className="sl-new-tag">New</span>}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Age/Height */}
                          <td>
                            <div className="sl-td-main">{item.age || 0} Yrs</div>
                            <div className="sl-td-sub">{item.height || 'N/A'}</div>
                          </td>

                          {/* Location */}
                          <td>
                            <div className="sl-td-main">{(item.location || "").split(',')[0] || 'N/A'}</div>
                            <div className="sl-td-sub">{(item.location || "").split(',')[1] || ''}</div>
                          </td>

                          {/* Education */}
                          <td>
                            <div className="sl-td-main">{item.education || 'N/A'}</div>
                            <div className="sl-td-sub">{item.occupation || 'N/A'}</div>
                          </td>

                          {/* Match */}
                          <td>
                            <span className="sl-match-badge">{item.matchPercentage || 0}%</span>
                          </td>

                          {/* Date */}
                          <td>
                            <div className="sl-td-main">{item.shortlistedOn || 'N/A'}</div>
                            <div className="sl-td-sub">{item.shortlistedTime || ''}</div>
                          </td>

                          {/* Actions */}
                          <td>
                            <div className="sl-action-group">
                              {/* ✅ CONDITIONAL INTEREST BUTTON */}
                              {isInterestSent ? (
                                <button
                                  className="sl-action-btn sl-btn-interest-sent"
                                  title="Interest Sent"
                                  disabled
                                >
                                  <FaHeart size={11} />
                                </button>
                              ) : !canInterest ? (
                                <button
                                  className="sl-action-btn sl-btn-lock"
                                  onClick={(e) => handleSendInterest(userId, e)}
                                  title="Plan Required"
                                >
                                  <FaLock size={11} />
                                </button>
                              ) : (
                                <button
                                  className="sl-action-btn sl-btn-heart"
                                  onClick={(e) => handleSendInterest(userId, e)}
                                  disabled={isLoading === 'interest'}
                                  title="Send Interest"
                                >
                                  {isLoading === 'interest' ? (
                                    <FaSpinner size={11} style={{ animation: 'slSpin 1s linear infinite' }} />
                                  ) : (
                                    <FaHeart size={11} />
                                  )}
                                </button>
                              )}

                              <button
                                className="sl-action-btn sl-btn-view"
                                onClick={(e) => { e.stopPropagation(); handleViewProfile(userId); }}
                                title="View Profile"
                              >
                                <FaEye size={11} />
                              </button>
                              <button
                                className="sl-action-btn sl-btn-del"
                                onClick={(e) => handleRemove(shortlistId, e)}
                                disabled={isLoading === 'remove'}
                                title="Remove"
                              >
                                {isLoading === 'remove' ? (
                                  <FaSpinner size={11} style={{ animation: 'slSpin 1s linear infinite' }} />
                                ) : (
                                  <FaTrash size={11} />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="sl-empty">
                <div className="sl-empty-icon">
                  <FaHeart />
                </div>
                <h3 className="sl-empty-title">No Shortlisted Profiles</h3>
                <p className="sl-empty-sub">You haven't shortlisted any profiles yet. Start exploring matches!</p>
                <button className="sl-empty-btn" onClick={() => navigate('/all-users')}>
                  💖 Find Matches
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <div className="sl-pagination">
              <div className="sl-page-info">
                Showing <strong>{startIndex + 1}-{Math.min(endIndex, filteredData.length)}</strong> of <strong>{filteredData.length}</strong> profiles
              </div>
              <div className="sl-page-btns">
                <button
                  className="sl-page-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FaArrowLeft size={10} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`sl-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => goToPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="sl-page-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <FaArrowRight size={10} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Shortlisted;